import { client } from "./supabase";

// ─── PLAN COBROS (facturas emitidas) ─────────────────────

export const getPlanCobros = async (facturaSiiEmitidaId) => {
  const { data, error } = await client
    .from("plan_cobros_facturas")
    .select("*")
    .eq("factura_sii_emitida_id", facturaSiiEmitidaId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

export const crearPlanCobros = async (cuotas) => {
  const { data, error } = await client.from("plan_cobros_facturas").insert(cuotas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaCobro = async (id, cambios) => {
  const { data, error } = await client.from("plan_cobros_facturas").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

export const eliminarPlanCobros = async (facturaSiiEmitidaId) => {
  const { error } = await client
    .from("plan_cobros_facturas")
    .delete()
    .eq("factura_sii_emitida_id", facturaSiiEmitidaId);
  if (error) throw error;
};

// ─── PLAN PAGOS (facturas recibidas) ─────────────────────

export const getPlanPagos = async (facturaSiiRecibidaId) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .select("*")
    .eq("factura_sii_recibida_id", facturaSiiRecibidaId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

export const crearPlanPagos = async (cuotas) => {
  const { data, error } = await client.from("plan_pagos_facturas").insert(cuotas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaPago = async (id, cambios) => {
  const { data, error } = await client.from("plan_pagos_facturas").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

export const eliminarPlanPagos = async (facturaSiiRecibidaId) => {
  const { error } = await client
    .from("plan_pagos_facturas")
    .delete()
    .eq("factura_sii_recibida_id", facturaSiiRecibidaId);
  if (error) throw error;
};

// Borra UNA cuota de pago por id, SOLO si está pendiente (el `.eq("estado","pendiente")` es el
// candado en DB). Nunca toca cuotas pagadas — están vinculadas a un movimiento histórico vía FK
// movimientos_bancarios.plan_pago_cuota_id. Si la cuota está pagada (o no existe), no borra nada
// y lanza error.
export const eliminarCuotaPago = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .delete()
    .eq("id", id)
    .eq("estado", "pendiente")
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar la cuota ${id} (¿ya está pagada?)`);
  return data[0];
};

// ─── Obtener todas las cuotas futuras pendientes ──────────

export const getCuotasCobrosFuturas = async (pais) => {
  const { data, error } = await client
    .from("plan_cobros_facturas")
    .select(
      `
      *,
      factura:facturas_sii_emitidas(
        folio, razon_social_receptor, pais
      )
    `
    )
    .eq("estado", "pendiente")
    .eq("factura.pais", pais)
    .order("fecha_vencimiento");
  if (error) throw error;
  const cuotas = data || [];
  if (cuotas.length === 0) return cuotas;

  // Enriquecer con `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado) para el
  // label "cuota N/M". El select de arriba filtra `estado='pendiente'`, así que su conteo daría
  // solo las pendientes (M mal); se cuenta aparte sin filtro de estado. Único consumidor:
  // FlujoCajaV2 (rama de cuotas de "Por cobrar").
  const facturaIds = [...new Set(cuotas.map((c) => c.factura_sii_emitida_id).filter((id) => id != null))];
  const { data: todasLasCuotas, error: errTotal } = await client
    .from("plan_cobros_facturas")
    .select("factura_sii_emitida_id")
    .in("factura_sii_emitida_id", facturaIds);
  if (errTotal) throw errTotal;
  const totalPorFactura = {};
  for (const row of todasLasCuotas || []) {
    totalPorFactura[row.factura_sii_emitida_id] = (totalPorFactura[row.factura_sii_emitida_id] || 0) + 1;
  }
  return cuotas.map((c) => ({ ...c, total_cuotas: totalPorFactura[c.factura_sii_emitida_id] || 0 }));
};

export const getCuotasPagosFuturas = async (pais) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .select(
      `
      *,
      factura:facturas_sii_recibidas(
        folio, razon_social_emisor, pais
      )
    `
    )
    .eq("estado", "pendiente")
    .eq("factura.pais", pais)
    .order("fecha_vencimiento");
  if (error) throw error;
  const cuotas = data || [];
  if (cuotas.length === 0) return cuotas;

  // Enriquecer con `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado) para el
  // label "cuota N/M". El select de arriba filtra `estado='pendiente'`, así que su conteo daría
  // solo las pendientes (M mal); se cuenta aparte sin filtro de estado. Único consumidor:
  // FlujoCajaV2 (rama de cuotas de "Por pagar").
  const facturaIds = [...new Set(cuotas.map((c) => c.factura_sii_recibida_id).filter((id) => id != null))];
  const { data: todasLasCuotas, error: errTotal } = await client
    .from("plan_pagos_facturas")
    .select("factura_sii_recibida_id")
    .in("factura_sii_recibida_id", facturaIds);
  if (errTotal) throw errTotal;
  const totalPorFactura = {};
  for (const row of todasLasCuotas || []) {
    totalPorFactura[row.factura_sii_recibida_id] = (totalPorFactura[row.factura_sii_recibida_id] || 0) + 1;
  }
  return cuotas.map((c) => ({ ...c, total_cuotas: totalPorFactura[c.factura_sii_recibida_id] || 0 }));
};
